import React, { useState, useEffect, useMemo, useRef } from "react";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { RadioButton, Input, NumericTextBox } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { useSelector, useDispatch } from "react-redux";
import {
    removeCondition,
    updateField,
    updateOperator,
    updateValue,
} from "../../redux/querySlice";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { determineFieldType, findDomain } from "./queryUtils";

const Condition = ({ dropDownArray, index, filteredData }) => {
    // Local state, causes component to rerender on update
    const [field, setField] = useState();
    const [operator, setOperator] = useState();
    const [value, setValue] = useState();
    const [domainData, setDomainData] = useState(undefined);
    const fieldTypeRef = useRef();

    const dispatch = useDispatch();
    const fullQuery = useSelector((state) => state.query);
    const queryData = filteredData;

    const handleFieldSelection = (e) => {
        const newField = e.value;
        dispatch(updateField([newField, [...index]]));
        dispatch(updateOperator([stringOperators[0], [...index]]));
        const fieldType = determineFieldType(queryData, e.value, fullQuery.dataSource);
        if (fieldType === "Date") {
            const date = Date.now();
            dispatch(updateValue([date, [...index]]));
        } else {
            dispatch(updateValue([undefined, [...index]]));
        }
    };

    const handleOperatorSelection = (e) => {
        const newOperator = e.value;
        dispatch(updateOperator([newOperator, [...index]]));
    };

    const handleValueSelection = (e) => {
        const newValue = e.value;
        dispatch(updateValue([newValue, [...index]]));
    };

    const handleDateSelection = (e) => {
        const dateNum = Date.parse(e.value);
        dispatch(updateValue([dateNum, [...index]]));
    };

    const handleDomainSelection = (e) => {
        const value = e.value.Code;
        dispatch(updateValue([value, [...index]]));
    };

    const handleRemoveCondition = () => {
        dispatch(removeCondition([...index]));
    };

    let disabled = false;
    const baseConditions = fullQuery.query.conditions.filter(
        (c) => c.logicOperator === undefined
    );

    if (baseConditions.length === 1 && index.length === 2) {
        disabled = true;
    }

    useEffect(() => {
        const condition = findConditionWithIndex(fullQuery, [...index]);
        const fieldType = determineFieldType(
            queryData,
            condition.field,
            fullQuery.dataSource
        );

        const domain = findDomain(queryData, condition.field, fullQuery.dataSource);
        if (domain !== undefined) setDomainData(domain);

        fieldTypeRef.current = fieldType;

        // handle date placeholder
        if (domain !== undefined) {
            // find domain value
            if (condition.value === undefined) {
                setValue("");
            } else {
                const domainValue = domain.find((v) => condition.value === v.Code);
                setValue(domainValue);
            }
        } else {
            if (fieldType === "Date") {
                const date = new Date(condition.value);
                setValue(date);
            } else if (fieldType === "String") {
                setValue(condition.value);
            }
            // if fieldType is number (Int or Double) set value to null instead of empty string
            else if (condition.value === "") {
                setValue(null);
            } else {
                setValue(condition.value);
            }
        }

        setField({ field: condition.field, type: fieldType });
        setOperator(condition.operator);
        return () => {
            setDomainData(undefined);
        };
    }, [fullQuery]);

    const itemRender = (li, itemProps) => {
        const itemChildren = (
            <span
                style={{
                    fontSize: "11px",
                }}>
                {" "}
                {li.props.children}
            </span>
        );
        return React.cloneElement(li, li.props, itemChildren);
    };

    return (
        <div className="condition-select query-dropdown">
            <DropDownList
                data={dropDownArray}
                value={field?.field}
                onChange={handleFieldSelection}
                itemRender={itemRender}
                popupSettings={{
                    className: "drop-down-class",
                    popupClass: "popup-class",
                }}
                size="small"
            />
            <DropDownList
                data={
                    domainData !== undefined
                        ? ["="]
                        : fieldTypeRef.current === "String"
                        ? stringOperators
                        : numericOperators
                }
                value={operator}
                onChange={handleOperatorSelection}
                popupSettings={{ popupClass: "query-dropdown-popup" }}
                size="small"
                itemRender={itemRender}
            />
            {domainData !== undefined ? (
                <DropDownList
                    data={domainData}
                    textField={"DisplayValue"}
                    dataItemKey={"Code"}
                    onChange={handleDomainSelection}
                    itemRender={itemRender}
                    value={value}
                />
            ) : field?.type === "Date" ? (
                <DatePicker onChange={handleDateSelection} value={value} />
            ) : field?.type?.toLowerCase() === "int" ||
              field?.type?.toLowerCase() === "smallinteger" ||
              field?.type?.toLowerCase() === "double" ||
              field?.type?.toLowerCase() === "integer" ||
              field?.type?.toLowerCase() === "number" ? (
                <NumericTextBox onChange={handleValueSelection} value={value} />
            ) : (
                <Input onChange={handleValueSelection} value={value} />
            )}
            <Button
                icon="close"
                disabled={disabled}
                onClick={handleRemoveCondition}
                style={{ right: "-5px" }}
                fillMode="flat"
            />
        </div>
    );
};

export default Condition;

const stringOperators = [
    "=",
    "!=",
    "contains",
    "does not contain",
    "starts with",
    "ends with",
];

const numericOperators = ["=", "!=", "<", "<=", ">", ">="];

const findConditionWithIndex = (state, indexArr) => {
    const i = indexArr.pop();
    const conditions = findConditionsWithIndex(state, indexArr);
    const condition = conditions[i];
    return condition;
};

const findConditionsWithIndex = (state, indexArr) => {
    let conditions = state.query.conditions;
    for (let i = 1; i < indexArr.length; i++) {
        conditions = conditions[indexArr[i]].conditions;
    }
    return conditions;
};
